import React, { useEffect, useMemo, useState, useCallback } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { styles } from "./style";
import { ImportError } from "type/import-error";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { VirtualizedTable } from "component/shared/table/VirtualizedTable";
import { TableData } from "../utils/validatedTableData";

interface OwnProps {
	tableData: TableData[];
	onLoad?: (valid: boolean, warn: boolean) => void;
	validate?: boolean;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

type Filter = "all" | "error" | "warn";

function commaDelimit(array: string[]) {
	return array.join(", ");
}

const TableDisplayComponent: React.FC<Props> = (props) => {
	const {
		classes = {},
		validate: shouldValidate,
		onLoad,
		tableData,
	} = props;

	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(250);
	const [filter, setFilter] = useState<Filter>("all");

	useEffect(() => {
		if (shouldValidate && onLoad && tableData.length > 0) {
			const valid = !tableData.some((data) => data.validationErrors);
			const warn = tableData.some((data) => data.validationWarnings);
			onLoad(valid, warn);
		}
		// eslint-disable-next-line
	}, [shouldValidate, onLoad, tableData]);

	const renderCellContent = useCallback(
		(
			field?: string | string[] | number | number[],
			error?: string,
			warn?: boolean
		) => {
			let content: React.ReactNode;
			if (field && Array.isArray(field) && field.length > 0) {
				content = commaDelimit(field.map((item) => item.toString()));
			}
			if (field && !Array.isArray(field)) {
				content = <>{field.toString()}</>;
			}
			return (
				<>
					{error ? (
						<Mui.Tooltip title={error} classes={{ tooltip: classes.tooltip }}>
							<Mui.Card
								elevation={0}
								className={warn ? classes.warningBackground : classes.errorBackground}
							>
								<Mui.Typography className={classes.address}>{content}</Mui.Typography>
							</Mui.Card>
						</Mui.Tooltip>
					) : (
						<Mui.Typography className={classes.address}>{content}</Mui.Typography>
					)}
				</>
			);
		},
		[classes]
	);

	const renderAddressContent = useCallback(
		(
			address: string = "",
			city: string = "",
			state: string = "",
			zip: string = ""
		) => (
			<>
				<Mui.Typography className={classes.address}>{address}</Mui.Typography>
				<Mui.Typography className={classes.address}>
					{`${city + " " + state + " " + zip}`}
				</Mui.Typography>
			</>
		),
		[classes]
	);

	const errors = tableData.some((data) => data.validationErrors);
	const warn = tableData.some((data) => data.validationWarnings);

	const filteredTableData = useMemo(() => {
		if (filter === "warn") {
			return tableData.filter((data) => data.validationWarnings);
		}
		if (filter === "error") {
			return tableData.filter((data) => data.validationErrors);
		}
		return tableData;
	}, [filter, tableData]);

	const columns = useMemo(
		() => [
			{
				id: "validate",
				width: 70,
				title: " ",
				value: (row: TableData) => {
					return (
						<>
							{shouldValidate && (
								<>
									{row.error ? (
										<>
											{row.error === ImportError.import && (
												<Icons.AlertCircle color="#f00" />
											)}
											{row.error === ImportError.action && (
												<Mui.Tooltip
													classes={{ tooltip: classes.tooltip }}
													title="This lead was imported but an action failed. Check the lead's profile for tags, subscriptions, etc."
												>
													<Icons.AlertTriangle color="#ff9800" />
												</Mui.Tooltip>
											)}
											{row.error === ImportError.duplicate && (
												<Mui.Tooltip
													classes={{ tooltip: classes.tooltip }}
													title="Duplicate record was skipped."
												>
													<PeopleAltOutlinedIcon style={{ color: "#ff9800" }} />
												</Mui.Tooltip>
											)}
										</>
									) : (
										<>
											{row.validationErrors && <Icons.AlertCircle color="#f00" />}
											{!row.validationErrors && row.validationWarnings && (
												<Icons.AlertTriangle color="#ff9800" />
											)}
											{!row.validationWarnings && !row.validationErrors && (
												<Icons.CheckCircle color="#4caf50" />
											)}
										</>
									)}
								</>
							)}
						</>
					);
				},
			},
			{
				id: "email",
				title: "Email",
				value: (row: TableData) => (
					<>
						{row.validationErrors?.email
							? renderCellContent(row.email, row.validationErrors?.email)
							: renderCellContent(row.email, row.validationWarnings?.email, true)}
					</>
				),
			},
			{
				id: "name",
				title: "Name",
				value: (row: TableData) => <>{renderCellContent(row.name)}</>,
			},
			{
				id: "phone",
				title: "Phone",
				value: (row: TableData) => <>{renderCellContent(row.phone)}</>,
			},
			{
				id: "address",
				title: "Address",
				width: 200,
				value: (row: TableData) => (
					<>{renderAddressContent(row.address, row.city, row.state, row.zip)}</>
				),
			},
			{
				id: "birthday",
				title: "Birthday",
				value: (row: TableData) => (
					<>{renderCellContent(row.birthday, row.validationErrors?.birthday)}</>
				),
			},
			{
				id: "contactType",
				title: "Type",
				value: (row: TableData) => (
					<>
						{renderCellContent(row.contactType, row.validationErrors?.contactType)}
					</>
				),
			},
			{
				id: "agentId",
				title: "AgentID",
				value: (row: TableData) => (
					<>
						{renderCellContent(row.agentId, row.validationWarnings?.agentId, true)}
					</>
				),
			},
			{
				id: "transactionAnniversary",
				title: "Anniversary",
				value: (row: TableData) => (
					<>
						{renderCellContent(
							row.transactionAnniversary,
							row.validationErrors?.transactionAnniversary
						)}
					</>
				),
			},
			{
				id: "pipelineType",
				title: "Pipeline",
				value: (row: TableData) => (
					<>
						{renderCellContent(row.pipelineType, row.validationErrors?.piplineType)}
					</>
				),
			},
			{
				id: "textMessageOptIn",
				title: "Texting",
				value: (row: TableData) => (
					<>{renderCellContent(row.textMessageOptIn ? "True" : undefined)}</>
				),
			},
			{
				id: "tags",
				title: "Tags",
				value: (row: TableData) => <>{renderCellContent(row.tags)}</>,
			},
			{
				id: "campaignIds",
				title: "CampaignIDs",
				value: (row: TableData) => (
					<>
						{renderCellContent(
							row.campaignIds,
							row.validationWarnings?.campaignIds,
							true
						)}
					</>
				),
			},
			{
				id: "marketIds",
				title: "MarketIDs",
				value: (row: TableData) => (
					<>
						{renderCellContent(
							row.marketIds,
							row.validationWarnings?.marketIds,
							true
						)}
					</>
				),
			},
			{
				id: "note",
				title: "Note",
				value: (row: TableData) => <>{renderCellContent(row.note)}</>,
			},
			{
				id: "leadSource",
				title: "Source",
				value: (row: TableData) => <>{renderCellContent(row.leadSource)}</>,
			},
		],
		[shouldValidate, classes, renderCellContent, renderAddressContent]
	);

	const handleFilterChange = useCallback(
		(value: Filter) => setFilter(value),
		[]
	);

	return (
		<Mui.Grid container spacing={1} direction="column">
			{(errors || warn) && (
				<Mui.Grid item>
					<ToggleButtonGroup
						value={filter}
						exclusive
						size="small"
						aria-label="text alignment"
					>
						<ToggleButton
							value="all"
							aria-label="left aligned"
							onClick={() => handleFilterChange("all")}
						>
							<Icons.FileText />
							<span style={{ marginLeft: 5 }}>Show All</span>
						</ToggleButton>
						<ToggleButton
							value="warn"
							aria-label="centered"
							onClick={() => handleFilterChange("warn")}
						>
							<Icons.AlertTriangle color="#ff9800" />
							<span style={{ marginLeft: 5 }}>Show Warnings</span>
						</ToggleButton>
						<ToggleButton
							value="error"
							aria-label="right aligned"
							onClick={() => handleFilterChange("error")}
						>
							<Icons.AlertCircle color="#f00" size={20} />
							<span style={{ marginLeft: 5 }}>Show Errors</span>
						</ToggleButton>
					</ToggleButtonGroup>
				</Mui.Grid>
			)}
			<Mui.Grid item>
				<Mui.Typography variant="subtitle1">
					*Address, City, State, and Zip have been combined for this preview. They
					are imported as individual fields.
				</Mui.Typography>
			</Mui.Grid>
			{tableData.length > 0 && (
				<Mui.Grid item style={{ width: "100%" }}>
					<VirtualizedTable
						items={filteredTableData}
						//fixedHeight={590}
						columns={columns}
						pageSize={pageSize}
						onPageSizeChange={setPageSize}
						page={page}
						onPageChange={setPage}
					/>
				</Mui.Grid>
			)}
		</Mui.Grid>
	);
};

export const TableDisplay = withStyles(TableDisplayComponent, styles);
